import isEmpty from 'lodash/isEmpty'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'
import { dateToString, localTimezone } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import {
  useRefreshShiftsRows,
  useRefreshStats,
} from 'modules/shift_management/hooks/refreshShiftsRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUI } from 'modules/shift_management/hooks/useUI'
import { ConflictsProps } from 'modules/shift_management/lib//types'
import Api from 'modules/shift_management/lib/Api'
import { ADD_SHIFT_OPTIONS } from 'modules/shift_management/lib/addShiftOptions'
import buildEmployeeIds from 'modules/shift_management/lib/buildEmployeeIds'
import { trackAddShiftSubmitEvent } from 'modules/shift_management/lib/trackEvents'
import RestDay from 'modules/shift_management/store/internal/models/RestDay'
import Shift from 'modules/shift_management/store/internal/models/Shift'
import type ShiftsRow from 'modules/shift_management/store/internal/models/ShiftsRow'

export interface Props {
  params: {
    selectedTemplateId: number
    selectedWeek?: number
    startDate: moment.Moment
    endDate: moment.Moment
    employeeIds: number[]
    notes: string
    locationId: number
    workAreaId: number
  }
  from: 'side_panel' | 'modal'
  onSuccess: (noConflicts: boolean, conflicts: ConflictsProps[]) => void
  contractVersions: ContractVersions
}

const useAssignTemplate = ({ params, from, onSuccess, contractVersions }: Props) => {
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [, dispatchUI] = useUI()

  const startDateParam = dateToString(params.startDate, 'backend')
  const endDateParam = dateToString(params.endDate, 'backend')
  const { refreshShiftsRows } = useRefreshShiftsRows(contractVersions)
  const { refreshStats } = useRefreshStats(startDateParam, endDateParam)
  const selectedTemplateId = params.selectedTemplateId
  const employeeIds = params.employeeIds
  const weekPosition = params.selectedWeek || 0
  const notes = params.notes
  const locationId = params.locationId
  const workAreaId = params.workAreaId

  const { running: submitting, performAction } = useAsyncAction(
    async () => {
      const result = await asyncAction(
        () =>
          Api.checkOverlappingShifts(
            selectedTemplateId,
            weekPosition,
            employeeIds,
            startDateParam,
            endDateParam
          ),
        {
          successFlash: false,
        }
      )

      if (isSuccessful(result)) {
        const { value: conflicts } = result
        const noConflicts = isEmpty(conflicts)

        if (noConflicts) {
          const result = await asyncAction(
            () =>
              Api.applyTemplate({
                viewStartDate: view.startAt,
                viewEndDate: view.endAt,
                selectedTemplateId,
                weekPosition,
                employeeIds,
                startDateParam,
                endDateParam,
                notes,
                locationId,
                workAreaId,
                timezone: localTimezone(),
              }),
            {
              successMessage: i18n.t('shift_management.actions.apply_rotating_shift_success'),
            }
          )

          if (isSuccessful(result)) {
            const { shifts: newShifts, rest_days: newRestDays } = result.value
            const shifts = newShifts.map((shift) => Shift.buildFromRequest(shift))
            const restDays = newRestDays.map((restDay) => RestDay.buildFromRequest(restDay))

            const shiftColors = shifts.map((shift) => shift.color)
            trackAddShiftSubmitEvent({
              view,
              employeeIds,
              shiftsCount: shifts.length,
              restDaysCount: restDays.length,
              shiftType: ADD_SHIFT_OPTIONS.template,
              color: shiftColors,
              from,
              withSearch: '',
              advancedOptions: false,
              extras: {
                template_id: selectedTemplateId,
                starting_template_week: weekPosition + 1, // for readability so first week is 1
              },
            })

            dispatchSchedule({ type: 'ADD_SHIFTS', shifts })
            dispatchShiftsRows({ type: 'ADD_SHIFTS', shifts, view })
            dispatchSchedule({ type: 'ADD_REST_DAYS', restDays })
            dispatchShiftsRows({ type: 'ADD_REST_DAYS', restDays })
            dispatchUI({ type: 'REFRESHING_STATS' })

            onSuccess(noConflicts, conflicts)

            const { employeeIds: newEmployeeIds, newShiftsRowsKeys } = buildEmployeeIds(
              view,
              shiftsRows,
              shifts
            )
            const previousShiftsRows = shiftsRows.filter((row: ShiftsRow) =>
              employeeIds.includes(row.id)
            )

            if (newShiftsRowsKeys.length) {
              await refreshShiftsRows(previousShiftsRows, newEmployeeIds)
            } else {
              await refreshStats(shiftsRows)
            }
          }
        } else {
          onSuccess(noConflicts, conflicts)
        }
      }
    },
    {
      successFlash: false,
      errorFlash: false,
    }
  )

  return {
    onSubmit: performAction,
    submitting,
  }
}

export default useAssignTemplate
